import React, { type ReactElement, useMemo } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { Route, Routes } from 'react-router-dom';
import { RedirectWithQuery } from 'feature-components/report-navigator/report-navigator';
import SystemErrorAlert from 'feature-components/system-error-alert';
import ClusterReportLayout from 'pages/clusters/layouts/cluster-report-layout';
import NoReportsAvailable from 'pages/clusters/subpages/aggregated-reports-tab/no-reports-available';
import ClusterCategoryReport from 'pages/clusters/subpages/aggregated-reports-tab/reports/category-report';
import ClusterItemAssessmentReport from 'pages/clusters/subpages/aggregated-reports-tab/reports/item-assessment-report';
import ClusterStanineReport from 'pages/clusters/subpages/aggregated-reports-tab/reports/stanine-report';
import ClusterYearLevelReport from 'pages/clusters/subpages/aggregated-reports-tab/reports/year-level-report';
import UnSupportedReport from 'pages/clusters/subpages/aggregated-reports-tab/un-supported-report';
import {
  type BaseAreaReport,
  useAreaReports
} from 'services/assist-api/entities/areas-reports/areas-reports';
import type { BaseClusterResponse } from 'services/assist-api/entities/test-instance-response/reports/customer-group/cluster-report-responses';
import {
  type BaseTestReport,
  useTestReports
} from 'services/assist-api/entities/tests-reports/tests-reports';
import { isError, isInitialLoad } from 'utils/check-query-status';

const reportMap = {
  'item-assessment': ClusterItemAssessmentReport,
  'year-level': ClusterYearLevelReport,
  stanine: ClusterStanineReport,
  category: ClusterCategoryReport
};

type Props = {
  period: { startDate: string; endDate: string };
  testIds: number[];
  areaId: number;
  queryResponses: UseQueryResult<BaseClusterResponse[], RequestError>;
};

/**
 * Display tabs to navigate between reports
 * and a route switch to match to the correct report
 */
export default function ClusterReportNavigator(props: Props): ReactElement {
  const { areaId, testIds, queryResponses } = props;
  const isTestView = testIds?.length === 1;

  const areaReports = useAreaReports(
    { filters: { area: areaId, allowCluster: true, visible: true } },
    { enabled: !isTestView }
  );

  const testReports = useTestReports(
    { filters: { test: testIds, allowCluster: true, visible: true } },
    { enabled: isTestView }
  );

  const clusterReports: (BaseTestReport | BaseAreaReport)[] = useMemo(() => {
    if (isTestView) return testReports.data?.json ?? [];
    return areaReports.data?.json ?? [];
  }, [areaReports.data?.json, isTestView, testReports.data?.json]);

  if (isInitialLoad(areaReports, testReports)) {
    return (
      <ClusterReportLayout.Report>
        <ClusterReportLayout.Tabs.Loading />
      </ClusterReportLayout.Report>
    );
  }

  if (isError(areaReports, testReports)) {
    return (
      <ClusterReportLayout.Report>
        <ClusterReportLayout.Tabs.Error />
        <SystemErrorAlert>Failed to retrieve reports.</SystemErrorAlert>
      </ClusterReportLayout.Report>
    );
  }

  const visibleReports = clusterReports
    .filter(({ visible }) => visible)
    .map(({ report }) => report);

  return (
    <ClusterReportLayout>
      <ClusterReportLayout.Tabs reports={visibleReports} />

      <Routes>
        {clusterReports.map((clusterReport: (typeof clusterReports)[0]) => {
          const { report } = clusterReport;
          const ClusterReport = reportMap[report.identifier as keyof typeof reportMap];

          return (
            <Route
              key={report.identifier}
              path={`/${report.identifier}/*`}
              element={
                <ClusterReport
                  identifier={report.identifier}
                  threshold={clusterReport.minimumResponseThreshold}
                  reportDescription={report.description}
                  {...props}
                  queryResponses={{ queries: [queryResponses], data: queryResponses.data }}
                />
              }
            />
          );
        })}

        {!!visibleReports.length && (
          // redirect to first supported report (if some are supported)
          <Route path="/" element={<RedirectWithQuery to={visibleReports[0].identifier} />} />
        )}

        {!visibleReports.length && (
          // when no reports are supported display message to convey this
          <Route path="/" element={<NoReportsAvailable isTestView={isTestView} />} />
        )}

        {/* when url has report segments that do not match convey this to the user */}
        <Route path="*" element={<UnSupportedReport />} />
      </Routes>
    </ClusterReportLayout>
  );
}
